<template>
    <form
        id="headerSearchV3"
        ref="headerSearchFormRef"
        class="header-search-form / flex"
        novalidate
        :class="{isDisplayMobile, zoomEffect: shouldAddZoomEffect}"
        @submit.prevent="handleSubmit"
    >
        <div class="header-search-form_inputs-container / flex" :class="{isFocused: isLocationFocused || isJobFocused}">
            <div class="search-input_wrapper search-input_wrapper_job / flex" :class="{isFocused: isJobFocused, isShadowed: isLocationFocused}">
                <JobAutocomplete />
            </div>
            <div v-if="!isDisplayMobile" class="search-input_wrapper_separation"></div>
            <div class="search-input_wrapper search-input_wrapper_location / flex" :class="{isFocused: isLocationFocused, isShadowed: isJobFocused}">
                <LocationAutocomplete :without-border="!isDisplayMobile" @focusin="isLocationFocused = true" @focusout="isLocationFocused = false" />
                <div class="header-search-form_submit_wrapper">
                    <VJoyIconButton
                        v-if="!isDisplayMobile"
                        class="header-search-form_submit_desktop"
                        type="submit"
                        variant="main"
                        size="small"
                        data-testid="header_search-form__submit"
                        title="Search"
                        icon="search"
                    />
                </div>
            </div>
        </div>
        <VJoyButton
            v-if="isDisplayMobile"
            class="header-search-form_submit_mobile"
            data-testid="header_search-form__submit"
            variant="main"
            type="submit"
        >
            {{ t('navbar.header.search.form.submit.button') }}
        </VJoyButton>
    </form>
</template>

<script setup lang="ts">
    import {navbarEventBus} from '@navbar/events';
    import {storeToRefs} from 'pinia';
    import {computed, useTranslation} from '#imports';
    import {useDisplayMobile} from '@malt/utils';
    import {VJoyButton, VJoyIconButton} from '@maltjoy/core-vue';
    import {useSearchFormStore} from '../search.store';
    import JobAutocomplete from './Inputs/JobAutocomplete.vue';
    import LocationAutocomplete from './Inputs/LocationAutocomplete.vue';

    const {t} = useTranslation();

    const {isDisplayMobile} = useDisplayMobile();

    const searchFormStore = useSearchFormStore();
    const {r$, isJobFocused, isLocationFocused, jobZoomEffect} = storeToRefs(searchFormStore);

    const shouldAddZoomEffect = computed(() => {
        return !isDisplayMobile.value && jobZoomEffect.value && isJobFocused.value;
    });

    function handleSubmit() {
        if (r$.value.$invalid) {
            if (r$.value.jobQuery.$invalid) {
                navbarEventBus.emit('focus-search-job-input');
            } else {
                navbarEventBus.emit('focus-search-location-input');
            }
        } else {
            searchFormStore.onSubmit();
            navbarEventBus.emit('navbar-search-form-submit');
        }
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text';
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .header-search-form {
        $form: &;
        --input-height: 46px;
        display: flex;
        flex-flow: row nowrap;
        margin: 0 auto;
        border: 1px solid var(--joy-color-neutral-30);
        border-radius: 100px;
        background-color: var(--joy-color-neutral-0);
        max-width: 100%;
        box-shadow: var(--joy-core-elevation-1);
        height: 48px;

        &.zoomEffect {
            animation: scaleEffect 1s ease 1;
        }

        &_inputs-container {
            position: relative;
            display: flex;
            flex-wrap: nowrap;
            border-radius: 100px;
            box-shadow: none;
            width: 100%;
            padding: 0;
            transition: width 0.3s ease;

            @include mq.screen_md(min) {
                &:hover {
                    .search-input_wrapper_location.isShadowed:not(.isFocused) {
                        :deep(.p-inputtext) {
                            background-color: var(--joy-color-neutral-20);
                        }
                    }
                    .search-input_wrapper_location {
                        :deep(input:hover) {
                            background-color: var(--joy-color-neutral-20);
                        }
                    }
                }
            }

            &.isFocused {
                @include mq.screen_md(min) {
                    &:hover {
                        background-color: var(--joy-color-neutral-20);
                        .search-input_wrapper_separation {
                            opacity: 0;
                        }
                    }
                }
                .search-input_wrapper_separation {
                    opacity: 0;
                }
                @include mq.screen_xs {
                    background-color: unset;
                }
            }
        }

        &_submit {
            height: 100%;

            &_wrapper {
                padding: 4px;
                height: 100%;
                position: absolute;
                right: 0;
                top: 0;
            }
        }

        .search-input_wrapper {
            border-radius: 100px;
            z-index: 1;
            transition: background-color 0.15s ease;
            height: 100%;

            @include mq.screen_md(min) {
                &.isShadowed:not(.isFocused) {
                    &:hover {
                        :deep(.p-inputtext) {
                            background-color: rgb(235, 235, 235);
                        }
                    }
                }
            }

            &_separation {
                width: 1px;
                inset: 0;
                height: 60%;
                top: 20%;
                background-color: var(--joy-color-neutral-30);
                transition: opacity 0.3s ease;
            }

            &_job {
                width: 202px;
                padding-left: 20px;
                padding-right: 20px;
            }

            &_location {
                @include mq.screen_xs {
                    padding-right: 20px;
                }
            }

            @include mq.screen_md(min) {
                &:hover:not(.isFocused) {
                    background-color: rgb(245, 245, 245);
                    &.isShadowed {
                        background-color: rgb(235, 235, 235);
                    }
                }
            }

            &.isFocused {
                background-color: var(--joy-color-neutral-0) !important;
                z-index: 2;
                box-shadow: var(--joy-core-elevation-2);
            }

            &:deep(input) {
                color: var(--joy-color-text-body);
            }
        }

        &.isDisplayMobile {
            padding: var(--joy-core-spacing-2);
            flex-flow: column nowrap;
            background-color: unset;
            border-radius: 0;
            border: none;
            box-shadow: none;
            gap: var(--joy-core-spacing-3);

            #{$form}_inputs-container {
                flex-flow: column nowrap;

                .search-input_wrapper {
                    width: 100%;
                    padding: 0;

                    .field-autocomplete {
                        display: block;
                        width: 100%;
                    }
                    &.isFocused {
                        box-shadow: none;
                    }
                }

                &.isFocused {
                    background-color: unset;
                }
            }
        }
    }

    :deep(.search-input) {
        width: 100%;
    }

    @keyframes scaleEffect {
        0% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
        }
    }
</style>
